import axios from "axios";
import { ensureFreshToken } from "../components/atsModule/services/session";

// ── WhatsApp, through our backend ───────────────────────────────────────────
//
// The browser holds no eNotify token. The backend sends from Kumudu's number:
//
//   - "Ticket Resolved" goes out by itself when a ticket is first resolved
//     (assignticket / updateticketsbulk). Their responses carry the outcome as
//     `whatsapp`; see whatsAppFailureText.
//   - Follow-ups and the Store Master group list use the routes below, which
//     need the signed session token and are open to Kumudu staff only.
const API = process.env.REACT_APP_API_BASE_URL;

const authHeaders = async () => {
  const token = await ensureFreshToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// The backend's own message when it sent one, which names the eNotify reason.
const reasonOf = (error, fallback) => {
  if (!error?.response) return "Couldn't reach the server.";
  return error.response.data?.message || fallback;
};

export const wpService = {
  /** Groups Kumudu's WhatsApp number is in, as { id, name, participants }. */
  async fetchGroups() {
    try {
      const res = await axios.get(`${API}/api/ticket/whatsapp-groups`, {
        headers: await authHeaders(),
      });
      return Array.isArray(res?.data?.data) ? res.data.data : [];
    } catch (error) {
      throw new Error(reasonOf(error, "WhatsApp groups could not be loaded."));
    }
  },

  /**
   * Send a follow-up to the ticket's store group and log it.
   * Resolves { followUpCount, logged }. Rejects only when nothing was sent.
   */
  async sendFollowUp({ ticketId, message }) {
    try {
      const res = await axios.post(
        `${API}/api/followup/send`,
        { ticket_id: ticketId, message },
        { headers: await authHeaders() },
      );
      return {
        followUpCount: res?.data?.data?.follow_up_count,
        logged: res?.data?.data?.logged !== false,
      };
    } catch (error) {
      throw new Error(reasonOf(error, "The follow-up could not be sent."));
    }
  },
};

/**
 * Warning text for the `whatsapp` outcome a resolve response carries, or null
 * when no message was due or it went out.
 */
export const whatsAppFailureText = (outcome, action = "Ticket resolved") =>
  outcome && outcome.sent === false
    ? `${action}, but the WhatsApp message wasn't sent. ${outcome.reason || ""}`.trim()
    : null;
